import React, { useState } from 'react';

const SYSTEMS_CONFIG = [
  {
    id: 'integumentary', label: 'Integumentary System', color: '#f59e0b', lightBg: '#fffbeb', border: '#fcd34d',
    items: [
      { id: 'skinStatus', label: 'Skin Status', options: ['Normal', 'Dry / scaly', 'Oedematous', 'Trophic changes', 'Maceration', 'Wound present'], normal: ['Normal'] },
      { id: 'pressureSores', label: 'Pressure Sores', options: ['None', 'Stage I', 'Stage II', 'Stage III', 'Stage IV', 'Healed / resolved'], normal: ['None', 'Healed / resolved'] },
    ]
  },
  {
    id: 'respiratory', label: 'Respiratory System', color: '#3b82f6', lightBg: '#eff6ff', border: '#93c5fd',
    items: [
      { id: 'rsStatus', label: 'RS Status', options: ['Normal', 'Compromised', 'Assisted ventilation'], normal: ['Normal'] },
      { id: 'secretions', label: 'Secretions', options: ['None', 'Mild', 'Moderate', 'Copious'], normal: ['None'] },
      { id: 'breathingPattern', label: 'Pattern of Breathing', options: ['Normal', 'Shallow', 'Paradoxical', 'Diaphragmatic dominance', 'Assisted'], normal: ['Normal'] },
      { id: 'chestDeformity', label: 'Chest Wall / Thoracic Deformity', options: ['None', 'Kyphosis', 'Scoliosis', 'Barrel chest', 'Pectus carinatum', 'Pectus excavatum'], normal: ['None'] },
    ]
  },
  {
    id: 'cardiovascular', label: 'Cardiovascular System', color: '#ef4444', lightBg: '#fef2f2', border: '#fca5a5',
    items: [
      { id: 'cvsStatus', label: 'CVS Status', options: ['Normal', 'Hypertensive', 'Hypotensive', 'Arrhythmia', 'Cardiac compromise'], normal: ['Normal'] },
      { id: 'dvt', label: 'Deep Vein Thrombosis', options: ['Absent', 'Suspected', 'Confirmed'], normal: ['Absent'] },
    ]
  },
  {
    id: 'musculoskeletal', label: 'Musculoskeletal System', color: '#8b5cf6', lightBg: '#f5f3ff', border: '#c4b5fd',
    items: [
      { id: 'contractures', label: 'Contractures', options: ['None', 'Mild', 'Moderate', 'Severe', 'Elbow', 'Wrist / Hand', 'Hip', 'Knee', 'Ankle (Equinus)', 'Equinovarus'], normal: ['None'] },
      { id: 'subluxations', label: 'Subluxations', options: ['None', 'Shoulder subluxation', 'Hip subluxation', 'Other joint'], normal: ['None'] },
      { id: 'jointMobility', label: 'Joint Mobility', options: ['Normal', 'Reduced', 'Severely restricted', 'Hypermobile'], normal: ['Normal'] },
      { id: 'otherPathology', label: 'Other Pathology', options: ['None', 'Osteoporosis', 'Heterotopic ossification', 'Fracture', 'Deformity'], normal: ['None'] },
    ]
  },
  {
    id: 'bladderBowel', label: 'Bladder & Bowel Functions', color: '#06b6d4', lightBg: '#ecfeff', border: '#67e8f9',
    items: [
      { id: 'incontinence', label: 'Bladder & Bowel Status', options: ['Continent', 'Urinary incontinence', 'Bowel incontinence', 'Both (urinary & bowel)', 'Catheterized (IDC)', 'Constipation', 'Retention'], normal: ['Continent'] },
    ]
  },
  {
    id: 'gastrointestinal', label: 'Gastrointestinal System', color: '#10b981', lightBg: '#ecfdf5', border: '#6ee7b7',
    items: [
      { id: 'giStatus', label: 'GI Status', options: ['Normal', 'Dysphagia', 'Nausea / vomiting', 'Reduced appetite', 'Nutritional compromise', 'PEG / NGT feeding'], normal: ['Normal'] },
    ]
  },
  {
    id: 'autonomic', label: 'Autonomic System', color: '#ec4899', lightBg: '#fdf2f8', border: '#f9a8d4',
    items: [
      { id: 'vasomotor', label: 'Vasomotor', options: ['Normal', 'Vasodilation', 'Vasoconstriction', 'Temperature dysregulation', 'Mottled skin'], normal: ['Normal'] },
      { id: 'pseudomotor', label: 'Pseudomotor (Sweating)', options: ['Normal', 'Hyperhidrosis', 'Anhidrosis', 'Localised sweating abnormality'], normal: ['Normal'] },
      { id: 'trophicChanges', label: 'Trophic Changes', options: ['None', 'Skin changes', 'Nail changes', 'Hair changes', 'Subcutaneous atrophy'], normal: ['None'] },
      { id: 'posturalHypotension', label: 'Postural Hypotension', options: ['Absent', 'Mild (asymptomatic)', 'Severe (symptomatic)', 'Managed with intervention'], normal: ['Absent'] },
      { id: 'rsd', label: 'Reflex Sympathetic Dystrophy', options: ['Absent', 'Suspected', 'Confirmed (CRPS)'], normal: ['Absent'] },
    ]
  },
];

const SystemsReviewAssessment = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(false);

  const toggle = (systemId, itemId, option) => {
    const sysVal = value[systemId] || {};
    const cur = sysVal[itemId] || [];
    const next = cur.includes(option) ? cur.filter(o => o !== option) : [...cur, option];
    onChange({ ...value, [systemId]: { ...sysVal, [itemId]: next } });
  };

  // Count abnormal systems
  const abnormalSystems = SYSTEMS_CONFIG.filter(sys =>
    sys.items.some(item => {
      const sel = (value[sys.id] || {})[item.id] || [];
      return sel.length > 0 && sel.some(s => !item.normal.includes(s));
    })
  ).length;

  const allNormal = SYSTEMS_CONFIG.every(sys =>
    sys.items.every(item => {
      const sel = (value[sys.id] || {})[item.id] || [];
      return sel.length === 0 || sel.every(s => item.normal.includes(s));
    })
  );

  const hasAny = SYSTEMS_CONFIG.some(sys =>
    sys.items.some(item => ((value[sys.id] || {})[item.id] || []).length > 0)
  );

  return (
    <div style={{ marginBottom: '8px' }}>
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} style={{
          width: '100%', padding: '15px', borderRadius: '14px',
          border: '1px solid #e2e8f0', backgroundColor: 'transparent', color: '#4338ca',
          fontSize: '0.95rem', fontWeight: '700', cursor: 'pointer',
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', transition: 'all 0.2s'
        }}>
          + Systems Review
          {abnormalSystems > 0 && (
            <span style={{ background: '#ef4444', color: '#fff', borderRadius: '12px', padding: '2px 10px', fontSize: '0.78rem', fontWeight: '700' }}>
              {abnormalSystems} system{abnormalSystems > 1 ? 's' : ''} abnormal
            </span>
          )}
          {hasAny && abnormalSystems === 0 && (
            <span style={{ background: '#10b981', color: '#fff', borderRadius: '12px', padding: '2px 10px', fontSize: '0.78rem', fontWeight: '700' }}>
              All normal
            </span>
          )}
        </button>
      ) : (
        <div style={{ padding: '20px' }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.3rem' }}>🏥</span>
              <span style={{ fontWeight: '800', fontSize: '1.1rem', color: '#3730a3' }}>Systems Review</span>
            </div>
            <button type="button" onClick={() => setOpen(false)} style={{
              background: '#e0e7ff', border: 'none', borderRadius: '50%', width: '32px', height: '32px',
              cursor: 'pointer', fontWeight: '700', color: '#4338ca', fontSize: '1.1rem'
            }}>×</button>
          </div>

          {/* Summary bar */}
          {hasAny && (
            <div style={{
              padding: '10px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '0.85rem', fontWeight: '600',
              background: allNormal ? '#ecfdf5' : '#fef2f2',
              color: allNormal ? '#065f46' : '#991b1b',
              border: `1px solid ${allNormal ? '#6ee7b7' : '#fca5a5'}`
            }}>
              {allNormal ? '✓ All systems within normal limits' : `⚠ ${abnormalSystems} system(s) with abnormal findings`}
            </div>
          )}

          {/* Systems */}
          {SYSTEMS_CONFIG.map(sys => {
            const sysVal = value[sys.id] || {};
            const sysAbnormal = sys.items.some(item => {
              const sel = sysVal[item.id] || [];
              return sel.length > 0 && sel.some(s => !item.normal.includes(s));
            });
            const sysAssessed = sys.items.some(item => (sysVal[item.id] || []).length > 0);

            return (
              <div key={sys.id} style={{
                marginBottom: '14px', borderRadius: '12px', padding: '14px',
                border: `1.5px solid ${sysAssessed ? sys.border : '#e2e8f0'}`,
                backgroundColor: sysAssessed ? sys.lightBg : '#fff'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{
                    fontWeight: '700', fontSize: '0.9rem', color: sys.color,
                    padding: '2px 10px', borderRadius: '8px',
                    background: sys.lightBg, border: `1px solid ${sys.border}`
                  }}>{sys.label}</span>
                  {sysAssessed && (
                    <span style={{
                      fontSize: '0.72rem', fontWeight: '700', borderRadius: '10px', padding: '1px 8px',
                      background: sysAbnormal ? '#fef2f2' : '#ecfdf5',
                      color: sysAbnormal ? '#dc2626' : '#059669',
                      border: `1px solid ${sysAbnormal ? '#fca5a5' : '#6ee7b7'}`
                    }}>
                      {sysAbnormal ? 'Abnormal' : 'Normal'}
                    </span>
                  )}
                </div>

                {sys.items.map(item => {
                  const sel = sysVal[item.id] || [];
                  return (
                    <div key={item.id} style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '0.78rem', fontWeight: '600', color: '#64748b', marginBottom: '6px' }}>{item.label}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {item.options.map(opt => {
                          const isNormal = item.normal.includes(opt);
                          const isActive = sel.includes(opt);
                          return (
                            <button key={opt} type="button" onClick={() => toggle(sys.id, item.id, opt)} style={{
                              padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s',
                              border: isActive
                                ? `2px solid ${isNormal ? '#059669' : '#dc2626'}`
                                : '2px solid #e2e8f0',
                              background: isActive ? (isNormal ? '#ecfdf5' : '#fef2f2') : '#f8fafc',
                              color: isActive ? (isNormal ? '#065f46' : '#991b1b') : '#64748b'
                            }}>
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Clear button */}
          {hasAny && (
            <button type="button" onClick={() => onChange({})} style={{
              marginTop: '8px', padding: '8px 20px', borderRadius: '10px',
              border: '1px solid #e2e8f0', background: '#f8fafc', color: '#64748b',
              fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer'
            }}>
              Clear All
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SystemsReviewAssessment;
